import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  ArrowLeft, FileText, CheckCircle2, AlertCircle, Search, Save,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

interface Conta {
  id: number
  fornecedor: string
  descricao: string
  valor: number
  vencimento: string | null
  tipo_documento: string | null
}

export default function LinhasDigitaveis() {
  const qc = useQueryClient()
  const [busca, setBusca] = useState('')
  const [linhas, setLinhas] = useState<Record<number, string>>({})
  const [tipos, setTipos] = useState<Record<number, string>>({})
  const [salvando, setSalvando] = useState<Record<number, boolean>>({})

  const { data: contas = [], isLoading } = useQuery<Conta[]>({
    queryKey: ['contas-sem-linha'],
    queryFn: async () => (await api.get('/financeiro/contas/sem-linha-digitavel', { params: { tipo: 'pagar' } })).data,
  })

  const filtradas = contas.filter(c =>
    !busca ||
    c.fornecedor.toLowerCase().includes(busca.toLowerCase()) ||
    c.descricao.toLowerCase().includes(busca.toLowerCase())
  )

  async function salvarLinha(cid: number) {
    const linha = (linhas[cid] || '').trim()
    if (!linha) {
      toast.error('Cole a linha digitável primeiro')
      return
    }
    setSalvando(s => ({ ...s, [cid]: true }))
    try {
      await api.post(`/financeiro/contas/${cid}/linha-digitavel`, {
        linha_digitavel: linha,
        tipo_documento: tipos[cid] || undefined,
      })
      toast.success('Linha salva')
      qc.invalidateQueries({ queryKey: ['contas-sem-linha'] })
      // Limpa do estado local
      setLinhas(l => { const n = { ...l }; delete n[cid]; return n })
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    } finally {
      setSalvando(s => ({ ...s, [cid]: false }))
    }
  }

  function inferirTipo(s: string): string {
    const digitos = s.replace(/\D/g, '')
    if (digitos.length === 47) return 'boleto'
    if (digitos.length === 48) {
      if (/8\d/.test(digitos.slice(0, 2))) return 'tributo'
    }
    return ''
  }

  return (
    <div className="p-4 lg:p-6 space-y-3 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <Link to="/financeiro/pagar" className="p-1.5 rounded hover:bg-ink-100">
          <ArrowLeft size={16} />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText size={22} className="text-violet-600" /> Cadastrar linhas digitáveis
          </h1>
          <p className="text-xs text-ink-500">
            Tiny v2 não traz boletos — cole a linha digitável de cada conta a pagar aqui.
            O sistema detecta o tipo (boleto/GNRE/DAS) automaticamente pelo número de dígitos.
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs">
        <strong>{contas.length}</strong> contas a pagar abertas <strong>sem linha digitável</strong>.
        Após cadastrar, use "Imprimir guias" em Contas a Pagar pra gerar PDF com código de barras.
      </div>

      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-400" />
        <input className="input pl-7 text-xs h-8" placeholder="Buscar fornecedor ou descrição..."
          value={busca} onChange={e => setBusca(e.target.value)} />
      </div>

      <div className="bg-white border border-ink-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-ink-50 sticky top-0">
            <tr className="text-[10px] uppercase font-bold text-ink-600">
              <th className="text-left p-2">Fornecedor</th>
              <th className="text-left p-2">Descrição</th>
              <th className="text-center p-2">Vencimento</th>
              <th className="text-right p-2">Valor</th>
              <th className="text-left p-2 w-28">Tipo</th>
              <th className="text-left p-2">Linha digitável (cole aqui)</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="p-8 text-center text-ink-400">Carregando...</td></tr>
            ) : filtradas.length === 0 ? (
              <tr><td colSpan={7} className="p-12 text-center text-ink-400 text-sm">
                <CheckCircle2 size={28} className="mx-auto mb-2 text-emerald-500" />
                Tudo cadastrado!
              </td></tr>
            ) : filtradas.map(c => {
              const linha = linhas[c.id] || ''
              const tipoInferido = linha ? inferirTipo(linha) : ''
              const tipoFinal = tipos[c.id] || tipoInferido || c.tipo_documento || ''
              return (
                <tr key={c.id} className="border-t border-ink-100">
                  <td className="p-2 truncate max-w-[160px]">{c.fornecedor}</td>
                  <td className="p-2 truncate max-w-[200px] text-[11px]">{c.descricao}</td>
                  <td className="text-center p-2 text-[10px]">
                    {c.vencimento ? new Date(c.vencimento).toLocaleDateString('pt-BR') : '—'}
                  </td>
                  <td className="text-right p-2 font-mono font-semibold">{formatMoney(c.valor)}</td>
                  <td className="p-2">
                    <select className="input text-[10px] h-7" value={tipoFinal}
                      onChange={e => setTipos(t => ({ ...t, [c.id]: e.target.value }))}>
                      <option value="">— auto —</option>
                      <option value="boleto">Boleto</option>
                      <option value="gnre">GNRE</option>
                      <option value="das">DAS</option>
                      <option value="fgts">FGTS</option>
                      <option value="darf">DARF</option>
                      <option value="ted">TED</option>
                    </select>
                  </td>
                  <td className="p-2">
                    <input className="input text-[10px] font-mono"
                      placeholder="Cole a linha de 47 ou 48 dígitos"
                      value={linha}
                      onChange={e => setLinhas(l => ({ ...l, [c.id]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') salvarLinha(c.id) }} />
                  </td>
                  <td className="p-2 text-center">
                    <button onClick={() => salvarLinha(c.id)}
                      disabled={!linha.trim() || salvando[c.id]}
                      className="text-[10px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white flex items-center gap-1 mx-auto">
                      {salvando[c.id] ? '...' : <><Save size={10} /></>}
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
